import { check_access } from 'access';
import { load_dynls } from 'dynls';
import { idleMonitor, send_to_welcome } from 'logout';

import { isThemeInjectorPayloadWithVariant } from '@dojo/framework/core/ThemeInjector';
// make generated .d.ts file valid
import { DNode } from '@dojo/framework/core/interfaces';
import { i18n } from '@dojo/framework/core/middleware/i18n';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { theme } from '@dojo/framework/core/middleware/theme';
import { create, dom, v, w } from '@dojo/framework/core/vdom';
import { Analytics } from '@holmescorp/analytics';
import { UpdateBanner } from '@holmescorp/announcements';
import { check_session_id } from '@holmescorp/cache-store';
import { delay } from '@holmescorp/common';
import {
	ditarootsChange,
	dynlsAddProcess,
	dynlsChangeProcess,
	get_topic_roots,
	initial_path,
	lccstate,
	load_lpl,
	load_roots,
	load_siteconfig,
	load_userstate,
	onlineChange,
	registry,
	site_status,
	themeContext,
	track_userstate,
	userstate
} from '@holmescorp/lcc-state';
import { logLp, logSystem } from '@holmescorp/logger';
import { subscribe } from '@holmescorp/pub-sub';

import { login } from '../support/login';
import { Content } from './Content';
import { Footer } from './Footer';
import { Heading } from './Heading';
import { Splash } from './splash';

// eslint-disable-next-line @typescript-eslint/no-unused-vars

export { ditarootsChange, onlineChange, registry };

interface AppIcache {
	loadingEle: HTMLElement | null;
	setup: boolean;
	needsSession: boolean | undefined;
	quizPattern: string | undefined;
}

const icache = createICacheMiddleware<AppIcache>();
const factory = create({ icache, theme, i18n, lccstate, userstate });

export const LccApp = factory(({ middleware: { icache, theme, /* i18n, */ lccstate, userstate } }) => {
	window.hcstatic.xapi_urn_prefix = 'urn:hc:';
	const needs_session = icache.getOrSet('needsSession', false);

	const setup = icache.getOrSet('setup', async (): Promise<boolean> => {
		logSystem.info(() => `using main.${window.hcstatic.hash}`);
		const lang = window.hcstatic.portal_language || 'en';

		await site_status();
		const { course_session, has_hccs } = window.hcstatic;

		// If has_hccs is undefined, the cookie check hasn't been implemented on the LCS
		// so check explicitly for 'false' here.
		if (has_hccs === false) {
			logSystem.info(() => `missing hccs cookie, so session id is unknown`);
			send_to_welcome();
		}
		const sessionid = course_session !== undefined && course_session.pk !== undefined ? course_session.pk : 0;
		await check_session_id(sessionid);

		logSystem.info(() => `verify access`);
		await check_access();

		const dynls = await load_dynls([]);
		logSystem.info(() => `load dynls`);
		lccstate.run(dynlsAddProcess)({ dynls });
		subscribe('refresh/dynlscache', async () => {
			const dynls = await load_dynls([]);
			lccstate.run(dynlsChangeProcess)({ dynls });
		});

		logSystem.info(() => `load LPL`);
		load_lpl();

		const config = await load_siteconfig();
		const version = config.bookversion;
		logSystem.info(() => `book version ${version}`);
		idleMonitor.idlelogout = config.login_timeout * 60;

		if (config.requires_session && sessionid === 0) {
			icache.set('needsSession', true);
			return false;
		}

		// set ditaroots
		const ditaroots = await get_topic_roots(version, lang);
		logSystem.info(() => `dita roots = ${ditaroots.join(', ')}`);

		await load_userstate();
		await load_roots(ditaroots, version, lang);
		logSystem.info(() => `setting up user state`);
		track_userstate();

		const actor = lccstate.get(lccstate.path('actor_xapi'));
		const loginTime = lccstate.get(lccstate.path('logintime'));
		const logoutTime = lccstate.get(lccstate.path('logoutTime'));
		login(actor, loginTime, logoutTime);

		const { get: uget, path: upath } = userstate;
		icache.getOrSet('quizPattern', uget(upath('quizPattern')));

		subscribe('change:quizpattern', (p, v) => {
			icache.set('quizPattern', v);
		});

		await delay(4 * 1000); // allow splash screen time to display
		setTimeout(() => {
			initial_path();
		}, 100);

		return true;
	});

	const loadingEle = icache.getOrSet('loadingEle', document.getElementById('loadingOverlay'));
	const hide_loading = (): DNode => {
		const rv = loadingEle && dom({ node: loadingEle, props: { styles: { display: 'none' } } });
		icache.set('loadingEle', null);
		return rv;
	};

	if (!theme.get()) {
		const themeData = themeContext.get();
		isThemeInjectorPayloadWithVariant(themeData)
			? theme.set(themeData.theme, themeData.variant)
			: themeData && theme.set(themeData.theme);
	}

	const quizPattern = icache.get('quizPattern');

	if (setup && !needs_session) {
		logLp.info(() => `rendering LCC app`);
		return v('div', { key: 'page', id: 'page', classes: [quizPattern && `quizpattern-${quizPattern}`] }, [
			w(UpdateBanner, {}),
			w(Heading, {}),
			w(Content, {}),
			w(Footer, {}),
			w(Analytics, {}),
			hide_loading()
		]);
	} else if (!setup && needs_session) {
		return v('div', { key: 'page', id: 'page', classes: [quizPattern && `quizpattern-${quizPattern}`] }, [
			w(UpdateBanner, {}),
			w(Heading, { showNav: false }),
			w(Content, { showNoSession: true }),
			w(Footer, {}),
			w(Analytics, {}),
			hide_loading()
		]);
	} else {
		return w(Splash, {});
	}
});
